/*******************
 * EXPERIMENT VIEWED — PAYLOAD ENRICHMENT
 *******************/
//
// Optimizely tells Segment when a user sees an experiment by calling
// analytics.track('Experiment Viewed', { ... }). We wrap Segment's track() so
// every Experiment Viewed call gets an optimizelyEndUserId property (from
// Optimizely's visitor API, falling back to its cookie). All Segment
// destinations receive the enriched version.
//
// LOAD ORDER MATTERS: layout/theme.liquid loads this file in <head>, BEFORE the
// Optimizely tag, as a plain blocking <script> — no async/defer. Optimizely
// loads async and can fire Experiment Viewed before the page has finished
// parsing; when this wrapper lived in the deferred analytics.js those early
// calls went out without optimizelyEndUserId (~10% of events).
//
// The file name is deliberately neutral (no "optimizely"/"experiment"/
// "analytics") so ad-blocker URL filters are less likely to block it.
//
// The wrapper also re-broadcasts the event as an `experiment:viewed` DOM event
// so the Datadog RUM module can forward it (RUM is not a Segment destination).
// Because that module loads later, each event is also kept on
// window.experimentViewedEvents and replayed when the listener attaches
// (src/lib/datadog/forwardExperimentViewed.js).
//
// Covered by src/scripts/eventContext.spec.js.
(function () {
  function getCookieValue(name) {
    var cookie = document.cookie.split('; ').find(function (value) {
      return value.startsWith(name + '=');
    });

    return cookie ? decodeURIComponent(cookie.slice(name.length + 1)) : null;
  }

  // Optimizely's own visitor id (same value as the optimizelyEndUserId cookie),
  // which still works when the cookie can't be read.
  function getOptimizelyVisitorId() {
    try {
      var optimizely = window.optimizely;
      if (!optimizely || typeof optimizely.get !== 'function') return null;
      var visitor = optimizely.get('visitor');
      return (visitor && visitor.visitorId) || null;
    } catch (e) {
      return null;
    }
  }

  // Prefer an id Optimizely put on the event, then its API, then the cookie.
  function enrichExperimentViewed(properties) {
    return Object.assign({}, properties, {
      optimizelyEndUserId:
        (properties && properties.optimizelyEndUserId) ||
        getOptimizelyVisitorId() ||
        getCookieValue('optimizelyEndUserId'),
    });
  }

  window.experimentViewedEvents = window.experimentViewedEvents || [];

  // Payloads already enriched and broadcast. When Segment's stub queues a call
  // and the loaded library replays it through the wrapped track(), the same
  // object comes back — skip it so RUM doesn't see the exposure twice.
  var seen = typeof WeakSet === 'function' ? new WeakSet() : null;

  function wrapTrack(analyticsInstance) {
    if (!analyticsInstance || typeof analyticsInstance.track !== 'function') return;
    if (analyticsInstance.__trackWrapped) return;

    var originalTrack = analyticsInstance.track.bind(analyticsInstance);

    analyticsInstance.track = function (eventName, properties, options, callback) {
      var isExperimentViewed = typeof eventName === 'string' && eventName.toLowerCase() === 'experiment viewed';
      if (isExperimentViewed && !(seen && properties && seen.has(properties))) {
        properties = enrichExperimentViewed(properties);
        if (seen) seen.add(properties);
        // Datadog RUM (src/lib/datadog/forwardExperimentViewed.js) listens for
        // experiment:viewed, but loads after this script. The list lets it
        // replay exposures that fired before its listener was attached.
        window.experimentViewedEvents.push(properties);
        window.dispatchEvent(new CustomEvent('experiment:viewed', { detail: properties }));
      }
      return originalTrack(eventName, properties, options, callback);
    };

    analyticsInstance.__trackWrapped = true;
  }

  var _analytics = window.analytics;

  Object.defineProperty(window, 'analytics', {
    configurable: true,
    // Wrap on read as well: Segment's snippet assigns an empty stub first and
    // only then attaches track(), so the setter alone misses the stub.
    get: function () {
      wrapTrack(_analytics);
      return _analytics;
    },
    set: function (newVal) {
      _analytics = newVal;
      wrapTrack(newVal);
    },
  });

  if (_analytics) wrapTrack(_analytics);
})();
